import { api } from './api';

export interface AppointmentSlot {
  datetime: string;
  available: boolean;
}

export interface Appointment {
  id: number;
  scheduled_at: string;
  name: string;
  email: string;
  phone: string;
  subject?: string;
  status: 'confirmed' | 'cancelled' | 'pending';
  ip_address?: string;
  created_at: string;
  updated_at: string;
}

export interface AppointmentSettings {
  id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateAppointmentData {
  scheduled_at: string;
  name: string;
  email: string;
  phone: string;
  subject?: string;
}

export interface CreateSettingsData {
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active?: boolean;
}

export const appointmentService = {
  getAvailableSlots: async (start: string, end: string): Promise<AppointmentSlot[]> => {
    try {
      const response = await api.get('/api/appointments/slots', { params: { start, end } });
      return response.data.slots;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des créneaux');
    }
  },

  createAppointment: async (data: CreateAppointmentData): Promise<Appointment> => {
    try {
      const response = await api.post('/api/appointments', data);
      return response.data.appointment;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la création du rendez-vous');
    }
  },

  getAllAppointments: async (page = 1, limit = 20, status?: string): Promise<{
    appointments: Appointment[];
    count: number;
    totalPages: number;
    currentPage: number;
  }> => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(status && { status })
      });
      const response = await api.get(`/api/appointments?${params}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des rendez-vous');
    }
  },

  cancelAppointment: async (id: number): Promise<Appointment> => {
    try {
      const response = await api.put(`/api/appointments/${id}/cancel`);
      return response.data.appointment;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de l\'annulation');
    }
  },

  deleteAppointment: async (id: number): Promise<void> => {
    try {
      await api.delete(`/api/appointments/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  },

  getSettings: async (): Promise<AppointmentSettings[]> => {
    try {
      const response = await api.get('/api/appointments/settings');
      return response.data.settings;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des paramètres');
    }
  },

  createSetting: async (data: CreateSettingsData): Promise<AppointmentSettings> => {
    try {
      const response = await api.post('/api/appointments/settings', data);
      return response.data.setting;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la création du paramètre');
    }
  },

  updateSetting: async (id: number, data: Partial<CreateSettingsData>): Promise<AppointmentSettings> => {
    try {
      const response = await api.put(`/api/appointments/settings/${id}`, data);
      return response.data.setting;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la mise à jour du paramètre');
    }
  },

  deleteSetting: async (id: number): Promise<void> => {
    try {
      await api.delete(`/api/appointments/settings/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erreur lors de la suppression du paramètre');
    }
  }
};
